import React from 'react';

import defaultPhoto from '@assets/img/arts/default-photo.png';
import pdfIcon from '@assets/img/icons/pdf.svg';
import { Commodity } from '@models/next/Commodity';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    count: (count: number) => `${count}枚`,
    clickView: '表示する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    position: 'relative',
    background: 'white',
  },
  emptyCoverBackground: {
    backgroundColor: theme.palette.grey[200],
    cursor: 'not-allowed',
  },
  border: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 0,
    borderColor: theme.palette.grey[300],
  },
  previewBox: {
    ...common.flexColumn,
    ...common.justifyEnd,
    position: 'absolute',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  previewCountBox: {
    ...common.flexCenter,
    ...common.middleText,
    position: 'absolute',
    right: '0px',
    bottom: '0px',
    left: '0px',
    height: '21px',
    backgroundColor: Color(theme.palette.grey[300]).alpha(0.85).toString(),
    pointerEvents: 'none',
  },
  previewCount: {
    color: theme.palette.grey[800],
  },
  defaultPhotoContainer: {
    ...common.flexCenter,
    width: '100%',
    height: '100%',
  },
  defaultPhoto: {
    width: '34px',
    height: '28px',
  },
  pdfContainer: {
    ...common.smallerText,
    ...common.textAlignCenter,
    ...common.flexColumnCenter,
    ...common.textGray500,
    flex: 1,
    rowGap: '1rem',
  },
}));

export interface CommodityPhotoCoverProps {
  commodity: Commodity;
  helperText?: string;
  width?: number;
  height?: number;
  countBoxHeight?: number;
  border?: boolean;
  onClick?: () => void;
}

export const CommodityPhotoCover: React.FC<CommodityPhotoCoverProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    commodity,
    helperText = strings.clickView,
    width = 99,
    height = 99,
    countBoxHeight = 21,
    border = false,
    onClick,
  } = props;

  const photoCount = commodity.images.length;
  const cover = photoCount > 0 ? commodity.images[0] : undefined;

  return (
    <div
      className={clsx(
        classes.container,
        border && classes.border,
        cover == null && classes.emptyCoverBackground,
      )}
      style={{ width, height }}
      onClick={onClick}
    >
      <div
        className={clsx(classes.previewBox, cover && classes.baseButton)}
        style={{
          backgroundImage: `url('${cover?.url}')`,
          top: border ? 8 : 0,
          right: border ? 14 : 0,
          bottom: border ? 8 : 0,
          left: border ? 14 : 0,
        }}
      >
        {cover == null && (
          <div className={classes.defaultPhotoContainer}>
            <img src={defaultPhoto} className={classes.defaultPhoto} />
          </div>
        )}
        {cover?.name.endsWith('.pdf') && (
          <div className={classes.pdfContainer}>
            <img src={pdfIcon} style={{ height: 32, width: 32 }} />
            <div
              style={{
                minHeight: 0,
                maxWidth: '100%',
                flexShrink: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cover.name}
            </div>
          </div>
        )}
      </div>
      {helperText && (
        <div className={classes.previewCountBox} style={{ height: `${countBoxHeight}px` }}>
          <a className={clsx(classes.previewCount, !photoCount && classes.textGray400Important)}>
            {photoCount !== undefined && photoCount >= 0 ? strings.count(photoCount) : helperText}
          </a>
        </div>
      )}
    </div>
  );
};
